import { css, cx } from "styled-system/css";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { DashboardEmpty } from "@/components/dashboard/dashboard-empty";
import { Button } from "@/components/ui/button";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { previewDepositAmount } from "@/lib/deposit-preview";
import { ServiceFormModal, type ServiceRow } from "@/components/dashboard/service-form-modal";

function ServiceListRow({ service, onEdit, onSaved }: { service: ServiceRow; onEdit: () => void; onSaved: () => void }) {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  async function toggleActive() {
    setBusy(true);
    await fetchWithSessionSync(
      `/api/dashboard/services/${service.id}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: !service.isActive }),
      },
      refresh,
    );
    setBusy(false);
    onSaved();
  }

  const deposit = previewDepositAmount(service);

  return (
    <li
      className={cx(
        cf.dashSurface,
        css({
          p: "4",
          display: "flex",
          flexDir: { base: "column", sm: "row" },
          justifyContent: "space-between",
          gap: { base: "4", sm: "3" },
          opacity: service.isActive ? 1 : 0.5,
        }),
      )}
    >
      <div className={css({ minW: 0 })}>
        <p className={css({ fontWeight: "semibold", color: "#0f172a" })}>
          {service.name}
          {!service.isActive && <span className={css({ ml: "2", fontSize: "xs", color: "#94a3b8" })}>(inactivo)</span>}
          {service.programOnly && (
            <span
              className={css({
                ml: "2",
                fontSize: "10px",
                fontWeight: "bold",
                letterSpacing: "0.04em",
                color: "#6d28d9",
                bg: "#ede9fe",
                px: "2",
                py: "0.5",
                rounded: "full",
                textTransform: "uppercase",
              })}
            >
              Solo programa
            </span>
          )}
        </p>
        {service.description ? (
          <p className={css({ fontSize: "sm", color: "#64748b", mt: "1" })}>{service.description}</p>
        ) : null}
        {deposit != null ? (
          <p className={css({ fontSize: "sm", color: "#b45309", mt: "1" })}>
            Seña: {(service.depositCurrency ?? "ARS") === "ARS" ? "$" : `${service.depositCurrency} `}
            {deposit}
          </p>
        ) : null}
      </div>
      <div
        className={css({
          display: "flex",
          flexDir: { base: "column", sm: "row" },
          gap: { base: "2.5", sm: "3" },
          alignItems: { base: "stretch", sm: "center" },
          flexShrink: 0,
        })}
      >
        <span
          className={css({
            fontSize: "sm",
            color: "#334155",
            whiteSpace: "nowrap",
            textAlign: { base: "left", sm: "right" },
          })}
        >
          {service.duration} min/turno
          {service.price != null && ` · $${service.price}`}
        </span>
        <div className={css({ display: "flex", gap: "2" })}>
          <Button
            variant="secondary"
            size="sm"
            onClick={onEdit}
            className={css({ flex: { base: "1", sm: "initial" }, h: { base: "11", sm: "auto" } })}
          >
            Editar
          </Button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void toggleActive()}
            className={css({
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: { base: "1", sm: "initial" },
              h: { base: "11", sm: "auto" },
              fontSize: "xs",
              px: "3",
              py: "1.5",
              rounded: "md",
              borderWidth: "1px",
              borderColor: service.isActive ? "#fca5a5" : "#86efac",
              color: service.isActive ? "#dc2626" : "#16a34a",
              bg: "white",
              cursor: "pointer",
              whiteSpace: "nowrap",
              _hover: { bg: service.isActive ? "#fef2f2" : "#f0fdf4" },
            })}
          >
            {service.isActive ? "Desactivar" : "Activar"}
          </button>
        </div>
      </div>
    </li>
  );
}

export function DashboardServices() {
  const { refresh } = useAuth();
  const [services, setServices] = useState<ServiceRow[] | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceRow | null>(null);

  const reload = useCallback(async () => {
    const r = await fetchWithSessionSync(
      "/api/dashboard/services?includeInactive=1",
      { credentials: "include" },
      refresh,
    );
    if (!r.ok) return;
    setServices((await r.json()) as ServiceRow[]);
  }, [refresh]);

  useEffect(() => {
    void reload();
  }, [reload]);

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(s: ServiceRow) {
    setEditing(s);
    setModalOpen(true);
  }

  if (!services) {
    return (
      <DashboardPageShell eyebrow="Catálogo" title="Servicios">
        <LoadingFrame variant="light" minHeight={280} />
      </DashboardPageShell>
    );
  }

  return (
    <DashboardPageShell
      eyebrow="Catálogo"
      title="Servicios"
      description="Definí qué ofrecés, precio y señas. Los clientes eligen estos servicios al reservar en tu página pública."
    >
      <div className={css({ display: "flex", justifyContent: "flex-end", mb: "5" })}>
        <Button variant="primary" onClick={openCreate} className={css({ w: { base: "full", sm: "auto" } })}>
          + Agregar servicio
        </Button>
      </div>

      {services.length === 0 ? (
        <DashboardEmpty
          title="Aún no cargaste servicios"
          hint="Usá el botón de arriba para crear el primero (nombre, duración y precio opcional)."
        />
      ) : (
        <ul className={css({ display: "flex", flexDir: "column", gap: "3" })}>
          {services.map((s) => (
            <ServiceListRow key={s.id} service={s} onEdit={() => openEdit(s)} onSaved={() => void reload()} />
          ))}
        </ul>
      )}

      <ServiceFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        service={editing}
        onSaved={() => void reload()}
      />
    </DashboardPageShell>
  );
}
